import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ShieldWarning, DeviceMobile, Copy, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Two-factor authentication (TOTP) settings page.
 *
 * Flow:
 *   1. GET /auth/2fa/status — decide whether to show "Enable" or "Disable"
 *   2. POST /auth/2fa/setup — fetch a new secret + QR
 *   3. User scans QR in Authenticator app, enters 6-digit OTP + password
 *   4. POST /auth/2fa/enable — server confirms + flips flag
 *   5. To disable, user re-enters OTP + password + POST /auth/2fa/disable
 */
export default function TwoFactor() {
  const [enabled, setEnabled] = useState(null); // null=loading
  const [setup, setSetup] = useState(null);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDisable, setShowDisable] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setEnabled(!!data.totp_enabled);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    if (!otp || !password) { toast.error("OTP and password required"); return; }
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { otp, password });
      toast.success("Two-factor authentication enabled");
      setSetup(null); setOtp(""); setPassword("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async () => {
    if (!otp || !password) { toast.error("OTP and password required"); return; }
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { otp, password });
      toast.success("Two-factor authentication disabled");
      setOtp(""); setPassword(""); setShowDisable(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const copySecret = () => {
    if (setup?.secret) {
      navigator.clipboard.writeText(setup.secret).then(() => toast.success("Secret copied"));
    }
  };

  return (
    <div data-testid="two-factor-page">
      <PageHeader
        title="Two-Factor Authentication"
        subtitle="Protect this account with a TOTP-based second factor (Google Authenticator, Authy, 1Password)."
        module="Security"
      />
      <div className="p-8 max-w-3xl">
        {enabled === null ? (
          <div className="text-slate-400">Loading…</div>
        ) : enabled ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-5">
            <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-1">
              <ShieldCheck size={18} weight="fill" /> 2FA is ON for this account
            </div>
            <div className="text-[13px] text-emerald-700 mb-4">
              You&apos;ll be prompted for a 6-digit code from your authenticator app every time you sign in.
            </div>
            {!showDisable ? (
              <Button
                variant="outline"
                onClick={() => setShowDisable(true)}
                data-testid="disable-2fa-button"
                className="rounded-sm border-red-300 text-red-700 hover:bg-red-50"
              >
                Disable 2FA
              </Button>
            ) : (
              <div className="space-y-3 max-w-sm">
                <div>
                  <Label className="label-caps mb-1 block">Current password</Label>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="disable-2fa-password" />
                </div>
                <div>
                  <Label className="label-caps mb-1 block">6-digit OTP</Label>
                  <Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="123 456" maxLength={7} data-testid="disable-2fa-otp" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={confirmDisable} disabled={busy} data-testid="confirm-disable-2fa" className="rounded-sm bg-red-600 hover:bg-red-700">
                    {busy ? "Disabling…" : "Confirm disable"}
                  </Button>
                  <Button variant="outline" onClick={() => { setShowDisable(false); setOtp(""); setPassword(""); }} className="rounded-sm">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : !setup ? (
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-center gap-2 text-slate-700 font-semibold mb-2">
              <ShieldWarning size={18} className="text-amber-500" weight="fill" /> 2FA is OFF
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Turning on 2FA prevents anyone with just your password from signing in.
              You&apos;ll need an authenticator app on your phone — <strong>Google Authenticator</strong>, <strong>Authy</strong>, or <strong>1Password</strong>.
            </p>
            <Button
              onClick={startSetup}
              disabled={busy}
              data-testid="enable-2fa-button"
              className="rounded-sm bg-[#0F172A] hover:bg-slate-800 text-white"
            >
              {busy ? "Preparing…" : "Enable 2FA"}
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5">
            <div>
              <div className="label-caps mb-2 flex items-center gap-2"><DeviceMobile size={13} /> Step 1 · Scan this QR</div>
              <div className="flex gap-4 items-start">
                <img src={setup.qr_data_url} alt="TOTP QR" className="w-48 h-48 border border-slate-200 bg-white" data-testid="totp-qr" />
                <div className="flex-1 text-[13px] text-slate-600 space-y-2">
                  <p>Open your authenticator app → tap <strong>Add</strong> → scan this code.</p>
                  <p>Can&apos;t scan? Enter this secret manually:</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono bg-slate-100 px-2 py-1 rounded-sm break-all">{setup.secret}</code>
                    <button onClick={copySecret} className="p-1 text-slate-500 hover:text-[#EA580C]" data-testid="copy-totp-secret" title="Copy secret">
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <div className="label-caps mb-3 flex items-center gap-2"><CheckCircle size={13} /> Step 2 · Confirm</div>
              <div className="grid grid-cols-2 gap-3 max-w-md">
                <div>
                  <Label className="label-caps mb-1 block">Password</Label>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="enable-2fa-password" />
                </div>
                <div>
                  <Label className="label-caps mb-1 block">6-digit code</Label>
                  <Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="123 456" maxLength={7} data-testid="enable-2fa-otp" />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={confirmEnable} disabled={busy} data-testid="confirm-enable-2fa" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">
                  {busy ? "Verifying…" : "Verify & enable"}
                </Button>
                <Button variant="outline" onClick={() => { setSetup(null); setOtp(""); setPassword(""); }} className="rounded-sm">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
